import {PublishEventData, SyncEventType} from '../types';

export type EventProcessingContext = {
  batchedUpdates: Partial<{
    currentTime: number;
    isPlaying: boolean;
    playbackRate: number;
  }>;
  newLeaderId: string | null;
  shouldTriggerLeaderElection: boolean;
  leaderElectionVideoId?: string;
};

/**
 * Create a new event processing context.
 */
export const createEventProcessingContext = (): EventProcessingContext => ({
  batchedUpdates: {},
  newLeaderId: null,
  shouldTriggerLeaderElection: false,
});

/**
 * Generic helper to apply partial updates from event data to context
 */
const applyPartialUpdates = (
  event: PublishEventData,
  context: EventProcessingContext
): void => {
  if (!event.data) {
    return;
  }

  if ('isPlaying' in event.data && event.data.isPlaying !== undefined) {
    context.batchedUpdates.isPlaying = event.data.isPlaying;
  }
  if ('currentTime' in event.data && event.data.currentTime !== undefined) {
    context.batchedUpdates.currentTime = event.data.currentTime;
  }
  if ('playbackRate' in event.data && event.data.playbackRate !== undefined) {
    context.batchedUpdates.playbackRate = event.data.playbackRate;
  }
};

/**
 * Process a PLAY event and update the context.
 */
export const processPlayEvent = (
  event: PublishEventData,
  context: EventProcessingContext
): void => {
  applyPartialUpdates(event, context);
};

/**
 * Process a PAUSE event and update the context.
 */
export const processPauseEvent = (
  event: PublishEventData,
  context: EventProcessingContext,
  currentLeaderId: string | null
): void => {
  // Check if this is an end-of-video pause
  if (
    event.type === SyncEventType.PAUSE &&
    event.data &&
    'isEndOfVideo' in event.data &&
    event.data.isEndOfVideo
  ) {
    // Treat this pause as an ended event - handle leader election
    if (event.videoId && event.videoId === currentLeaderId) {
      context.shouldTriggerLeaderElection = true;
      context.leaderElectionVideoId = event.videoId;
    }
    // Don't update isPlaying for end-of-video pauses
  } else {
    applyPartialUpdates(event, context);
  }
};

/**
 * Process a SEEKING event and update the context.
 */
export const processSeekingEvent = (
  event: PublishEventData,
  context: EventProcessingContext,
  currentLeaderId: string | null
): void => {
  if (event.videoId === currentLeaderId) {
    applyPartialUpdates(event, context);
  }
};

/**
 * Process a SEEKED event and update the context.
 */
export const processSeekedEvent = (
  event: PublishEventData,
  context: EventProcessingContext
): void => {
  applyPartialUpdates(event, context);
};

/**
 * Process a TIME_UPDATE event and update the context.
 */
export const processTimeUpdateEvent = (
  event: PublishEventData,
  context: EventProcessingContext,
  currentLeaderId: string | null
): void => {
  if (event.videoId && currentLeaderId === event.videoId) {
    applyPartialUpdates(event, context);
  }
};

/**
 * Process a RATE_CHANGE event and update the context.
 */
export const processRateChangeEvent = (
  event: PublishEventData,
  context: EventProcessingContext
): void => {
  applyPartialUpdates(event, context);
};

/**
 * Process a LEADER event and update the context.
 */
export const processLeaderEvent = (
  event: PublishEventData,
  context: EventProcessingContext
): void => {
  if (event.data && 'videoId' in event.data) {
    context.newLeaderId = event.data.videoId;
  }
  applyPartialUpdates(event, context);
};

/**
 * Process an ENDED event and update the context.
 */
export const processEndedEvent = (
  event: PublishEventData,
  context: EventProcessingContext,
  currentLeaderId: string | null
): void => {
  // Handle leader election for ended events
  // If the ended video is the current leader, we need to elect a new leader
  if (event.videoId && event.videoId === currentLeaderId) {
    context.shouldTriggerLeaderElection = true;
    context.leaderElectionVideoId = event.videoId;
  }
  // If a non-leader video ends, we don't need to do anything special
  // The current leader should continue as normal
};

/**
 * Process any event type and update the context accordingly.
 */
export const processEvent = (
  event: PublishEventData,
  context: EventProcessingContext,
  currentLeaderId: string | null
): boolean => {
  switch (event.type) {
    case SyncEventType.PLAY:
      processPlayEvent(event, context);
      break;
    case SyncEventType.PAUSE:
      processPauseEvent(event, context, currentLeaderId);
      break;
    case SyncEventType.SEEKING:
      processSeekingEvent(event, context, currentLeaderId);
      break;
    case SyncEventType.SEEKED:
      processSeekedEvent(event, context);
      break;
    case SyncEventType.TIME_UPDATE:
      processTimeUpdateEvent(event, context, currentLeaderId);
      break;
    case SyncEventType.RATE_CHANGE:
      processRateChangeEvent(event, context);
      break;
    case SyncEventType.LEADER:
      processLeaderEvent(event, context);
      break;
    case SyncEventType.ENDED:
      processEndedEvent(event, context, currentLeaderId);
      break;
  }
  return true; // Allow all other events
};
